import { Box, Divider, IconButton, Modal } from '@zillow/constellation-10';
import { IconExpandFilled } from '@zillow/constellation-icons';
import { handleSystemMessageCtaClick } from './handleSystemMessageCtaClick';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { AppContext } from '../../../contexts/AppContextProvider';
import { useSatelliteWebLandlordConversationHandler_markConversationAsRead } from '@zg-rentals/ts-satellite-client-react-query';
import { useConversationMessagesQuery } from '../../../hooks/useConversationMessagesQuery';
import { LeadManagementContext } from '../../../contexts/LeadManagementContextProvider';
import useDevices from '../../../hooks/useDevices';
import useLeadManagementActions from '../../../hooks/useLeadManagementActions';
import useModal from '../../../hooks/useModal';
import useQueryParamsNext from '../../../hooks/useQueryParamsNext';
import { useCachedLeadByConversationId, useOptimisticLeadUpdate } from '../../../hooks/useLeadManagementTableQuery';
import useLeadRestrictions from '../../../hooks/useLeadRestrictions';
import { canAttachToConversation, canSendToConversation } from '../../../utils/composerRestrictions';
import { SatelliteConversationFeatureType } from '@zg-rentals/ts-satellite-client';
import { ModalType } from '../../../types/ModalType';
import RenterMessagingModalHeader from './components/RenterMessagingModalHeader';
import SwitchConversationChip from './components/SwitchConversationChip';
import MinimizedMessagingPill from './components/MinimizedMessagingPill';
import { leadManagementMinimizedMessagingConversationIdStore } from '../../../lib/leadManagementStores';
import useMessagingWindowSize, {
  MESSAGING_WINDOW_MIN_SIZE,
  VIEWPORT_MARGIN,
} from '../../../hooks/useMessagingWindowSize';
import useRestoreMessagingWindow from '../../../hooks/useRestoreMessagingWindow';
import AddMessage from '../../AddMessage';
import MessageListActions from '../../MessageListActions';
import MessageListItems from '../../MessageListItems';
import type { OnSystemMessageCtaClickArgs } from '../../MessageItem';
import { useAuth } from '../../../AuthProvider';
import { emitClickOnViewInMessagesIcon } from '../../../constants/clickstreamEvents/leadManagementMessageWindow';
import { emitRenterProfileChatBubbleAboutMeClickEvent } from '../../../constants/clickstreamEvents/renterProfile';
import { TRIGGER_LOCATION } from '../../../constants/clickstreamEvents/globals';
import {
  modalBodyStyles,
  modalContentWrapperStyles,
  resizeGripStyles,
  resizeLeftEdgeStyles,
  resizeTopEdgeStyles,
} from './styles';

// Drawer width must match ../RenterDetailsModal/styles.ts. The window's flag-off dimensions and viewport
// spacing come from the resize hook's constants so its floor can't desync from the CSS here.
const DRAWER_WIDTH = 720;

// Persistent widget alongside the page: modal focus mode would aria-hide the leads table and
// site nav. closeOnFocusOut is inert here (no onOpenChange) but keeps the two dialogs consistent,
// so adding a handler later can't silently make focus dismiss the window. Extracted with
// `satisfies` because the @ts-expect-error at the call site would otherwise let a misspelled key
// through and silently drop the override.
const focusManagerProps = {
  modal: false,
  closeOnFocusOut: false,
} satisfies { modal: boolean; closeOnFocusOut: boolean };

type RenterMessagingModalProps = {
  conversationId: string;
};

export default function RenterMessagingModal({ conversationId }: RenterMessagingModalProps) {
  const { updateQueryParams } = useQueryParamsNext();
  const { isDesktopView } = useDevices();
  const { setActiveModal } = useModal();
  const { user } = useAuth();
  const { paths } = useContext(AppContext);
  const { conversationId: renterDetailsConversationId, clientTimeZone } = useContext(LeadManagementContext);
  const { onAddMessageCompletedCacheUpdate } = useLeadManagementActions({ conversationId });
  const restoreMessagingWindow = useRestoreMessagingWindow();
  const lead = useCachedLeadByConversationId(conversationId);
  const { leadId } = lead?.renterInfo || {};
  const activeListingAlias = lead?.listingDetails?.listingAlias ?? '';
  const optimisticLeadUpdate = useOptimisticLeadUpdate();

  // Single fetch for the modal: MessageListItems below also subscribes to this same React Query key,
  // so the data is shared (no double-fetch) and the modal body and footer stay in sync.
  const { data: conversationData, isError: hasConversationError } = useConversationMessagesQuery({
    conversationId,
    clientTimeZone,
  });
  const {
    canSendMessage: isSendAllowedByRestrictions,
    getDisabledMessage,
    isRestrictionRolloutEnabled,
  } = useLeadRestrictions(conversationData ?? null);
  const canSendMessage = canSendToConversation({
    isSendAllowedByRestrictions,
    isRestrictionRolloutEnabled,
    hasConversationError,
    hasConversationData: !!conversationData,
  });
  const sendDisabledMessage = getDisabledMessage(SatelliteConversationFeatureType.SEND_MESSAGE);

  const isMinimized = !!leadManagementMinimizedMessagingConversationIdStore.useValue();
  // Resizing is a desktop affordance: below the desktop breakpoint the window keeps its fixed
  // layout, so a size dragged out on a big monitor can't follow the user onto a phone, and the
  // drag handles never become stray touch targets.
  const isResizable = isDesktopView;

  // Render-assigned so the unmount cleanup below always calls the latest instance without
  // re-arming the effect (a dep change would run the cleanup mid-life and un-minimize).
  const restoreMessagingWindowRef = useRef(restoreMessagingWindow);
  restoreMessagingWindowRef.current = restoreMessagingWindow;
  // Closing the window (unmount) resets the minimized state so the next open starts expanded —
  // via the restore path, so a window unmounted while minimized (drawer opening below the desktop
  // breakpoint, page navigation) still invalidates its stale thread.
  useEffect(() => () => restoreMessagingWindowRef.current(), []);

  // Position the window to the left of the Renter Details drawer when it's open.
  const isRenterDetailsDrawerOpen = !!renterDetailsConversationId;
  const rightPosition = isRenterDetailsDrawerOpen ? DRAWER_WIDTH + VIEWPORT_MARGIN : VIEWPORT_MARGIN;
  const {
    size: windowSize,
    getResizeHandleProps,
    handleResizeKeyDown,
    persistKeyboardResize,
  } = useMessagingWindowSize({ reservedRight: rightPosition + VIEWPORT_MARGIN, enabled: isResizable });

  // Invalidate leadManagement:table on success so the unread red dot in the lead row
  // clears after the BE confirms. Also invalidate conversationCounts to keep the inbox
  // unread badge in sync with what the inbox flow does (see useConversationActions.tsx).
  const { mutate: markConversationAsRead } = useSatelliteWebLandlordConversationHandler_markConversationAsRead({
    meta: {
      invalidates: ['leadManagement:table', 'satellite:conversationCounts'],
      context: {
        api: 'markConversationAsRead',
        details: 'RenterMessagingModal - marking conversation as read on scroll-to-bottom',
      },
    },
  });
  const hasMarkedAsReadRef = useRef(false);
  const lastMarkedMessageIdRef = useRef<string | null>(null);

  const fullMessageCountNumber = conversationData?.pagination.queryFullMessageCount ?? 0;
  const hasQuickResponses = conversationData?.hasQuickResponses ?? false;
  const quickResponses = conversationData?.quickResponses ?? null;
  const isConversationBlockedByZillow = conversationData?.isConversationBlockedByZillow ?? false;
  // `isPhoneLead` isn't returned by `getMessages`; pull it from the lead in cache instead.
  const isPhoneLead = lead?.latestContact?.isPhoneLead ?? false;
  const isSpam = conversationData?.status?.isSpam ?? false;
  // Shared with AddMessage's own attach gate so the drag-to-attach drop zone can't drift from
  // the attach button beside it.
  const canAttach = canAttachToConversation({
    canSendMessage,
    isPhoneLead,
    isSpam,
    isConversationBlockedByZillow,
  });

  // The latest server-side message id in the conversation. Optimistic temp messages
  // (`temp-…`, set by AddMessage while a send is in flight) are excluded so a user-sent
  // message in flight doesn't trigger the mark-as-read guard reset.
  const latestServerMessageId =
    conversationData?.conversation
      ?.slice()
      .reverse()
      .find((m) => !m.messageId?.startsWith('temp-'))?.messageId ?? null;

  // When a new message arrives via WebSocket while the modal is open, the latest
  // message id changes — reset the one-shot guard so the next scroll-to-bottom marks
  // the new message as read. (We track the latest id rather than `pagination.queryFullMessageCount`
  // because that field is not reliably populated by the getMessages endpoint.)
  useEffect(() => {
    if (lastMarkedMessageIdRef.current === null) return;
    if (latestServerMessageId && latestServerMessageId !== lastMarkedMessageIdRef.current) {
      hasMarkedAsReadRef.current = false;
    }
  }, [latestServerMessageId]);

  const openRenterDetailsDrawer = useCallback(() => {
    updateQueryParams({ queryParamValues: { conversationId }, shouldReplaceHistory: true });
  }, [updateQueryParams, conversationId]);

  const handleScrollComplete = useCallback(() => {
    if (hasMarkedAsReadRef.current) return;
    hasMarkedAsReadRef.current = true;
    lastMarkedMessageIdRef.current = latestServerMessageId;

    const rollback = optimisticLeadUpdate(conversationId, (lead) => ({
      ...lead,
      latestContact: { ...lead.latestContact, isRead: true },
    }));

    markConversationAsRead(
      { postData: { conversationId, lastReadMs: Date.now() } },
      {
        onError: () => {
          hasMarkedAsReadRef.current = false;
          rollback();
        },
      },
    );
  }, [markConversationAsRead, conversationId, optimisticLeadUpdate, latestServerMessageId]);

  const handleRenterProfileClick = useCallback(() => {
    emitRenterProfileChatBubbleAboutMeClickEvent({
      user,
      listing: { listingAlias: activeListingAlias },
      triggerLocation: TRIGGER_LOCATION.LeadManagementPage,
      lead: { leadId },
    });
    openRenterDetailsDrawer();
  }, [user, activeListingAlias, leadId, openRenterDetailsDrawer]);

  const onSystemMessageCtaClick = useCallback(
    (args: OnSystemMessageCtaClickArgs) => {
      handleSystemMessageCtaClick(args, openRenterDetailsDrawer, paths?.leadManagementPage);
    },
    [openRenterDetailsDrawer, paths?.leadManagementPage],
  );

  // Don't render if Renter Details drawer is open and screen is smaller than desktop breakpoint
  if (isRenterDetailsDrawerOpen && !isDesktopView) {
    return null;
  }

  const handleClose = () => {
    updateQueryParams({
      queryParamValues: {
        messagingConversationId: null,
      },
      shouldReplaceHistory: true,
    });
  };

  const handleOpenInInbox = () => {
    emitClickOnViewInMessagesIcon(user, { listingAlias: activeListingAlias }, { leadId });
  };

  // The pill replaces the whole window (thread unmounts): scroll-driven mark-as-read can't fire
  // while minimized, and the unread dot updates via the lead-table cache instead.
  if (isMinimized) {
    return (
      <MinimizedMessagingPill
        conversationId={conversationId}
        rightPosition={rightPosition}
        onRestore={restoreMessagingWindow}
      />
    );
  }

  const bodyContent = (
    <MessageListItems
      conversation={{ conversationId, listingAlias: activeListingAlias, clientTimeZone }}
      leadId={leadId}
      isPhoneLead={isPhoneLead}
      onRenterProfileClick={handleRenterProfileClick}
      onScrollComplete={handleScrollComplete}
      onSystemMessageCtaClick={onSystemMessageCtaClick}
      triggerLocation={TRIGGER_LOCATION.LeadManagementPage}
    />
  );

  const footerContent = (
    <AddMessage
      variant="compact"
      conversationId={conversationId}
      listingAlias={activeListingAlias}
      leadId={leadId}
      clientTimeZone={clientTimeZone}
      fullMessageCountNumber={fullMessageCountNumber}
      hasQuickResponses={hasQuickResponses}
      quickResponses={quickResponses}
      isConversationBlockedByZillow={isConversationBlockedByZillow}
      isPhoneLead={isPhoneLead}
      isSpam={isSpam}
      canSendMessage={canSendMessage}
      sendDisabledMessage={sendDisabledMessage}
      triggerLocation={TRIGGER_LOCATION.LeadManagementPage}
      onAddMessageCompletedCacheUpdate={onAddMessageCompletedCacheUpdate}
    />
  );

  // `group` powers the grip's hover reveal; the data attribute is the resize hook's measurement
  // anchor. Both are omitted with the flag off so that DOM stays untouched.
  const resizableContentProps = isResizable ? { className: 'group', 'data-messaging-window': '' } : {};

  return (
    <Modal.Root open useDismissProps={{ escapeKey: false, outsidePress: false }}>
      <Modal.Portal>
        <Modal.Content
          {...resizableContentProps}
          // @ts-expect-error - focusManagerProps is typed as the full FloatingFocusManagerProps,
          // whose children/context Modal.Content supplies itself, so a partial override can't typecheck.
          focusManagerProps={focusManagerProps}
          style={{
            // NOTE: Positioning and sizing live on the style prop, not css: the width css prop
            // can't be overridden in Modal.Content, and Panda can't statically extract the resize
            // hook's imported constants — inline values keep the flag-off floor and the hook's
            // MESSAGING_WINDOW_MIN_SIZE from ever desyncing.
            right: `${rightPosition}px`,
            bottom: `${VIEWPORT_MARGIN}px`,
            // Keep the widget below full-screen modals (e.g. image preview) that use the 'modal' z-index token
            zIndex: 1000,
            ...(isResizable
              ? {
                  width: `${windowSize.width}px`,
                  height: `${windowSize.height}px`,
                  maxWidth: 'none',
                  // The top-nav offset is a calc() CSS var, so it can't be resolved to a number in JS —
                  // cap the height here so a resized window can never run under the fixed top bar.
                  maxHeight: `calc(100vh - var(--ia-topnav-total-height, 0px) - ${2 * VIEWPORT_MARGIN}px)`,
                }
              : {
                  width: `${MESSAGING_WINDOW_MIN_SIZE.width}px`,
                  height: `${MESSAGING_WINDOW_MIN_SIZE.height}px`,
                  maxWidth: `${MESSAGING_WINDOW_MIN_SIZE.width}px`,
                }),
          }}
          css={{
            position: 'fixed',
            animation: 'none',
          }}
        >
          {isResizable && (
            <>
              <Box css={resizeTopEdgeStyles} {...getResizeHandleProps('y')} />
              <Box css={resizeLeftEdgeStyles} {...getResizeHandleProps('x')} />
              <IconButton
                title="Resize chat window"
                icon={<IconExpandFilled />}
                shape="circle"
                size="xs"
                emphasis="bare"
                css={resizeGripStyles}
                {...getResizeHandleProps('both')}
                onKeyDown={handleResizeKeyDown}
                onKeyUp={persistKeyboardResize}
                onBlur={persistKeyboardResize}
              />
            </>
          )}
          <Modal.Header css={{ paddingRight: 'default', paddingBlock: 'tight' }}>
            <RenterMessagingModalHeader
              conversationId={conversationId}
              clientTimeZone={clientTimeZone}
              listingAlias={activeListingAlias}
              onClose={handleClose}
              onOpenInInbox={handleOpenInInbox}
              onMinimize={() => leadManagementMinimizedMessagingConversationIdStore.set(conversationId)}
            />
          </Modal.Header>
          <Divider />
          <Box
            css={modalContentWrapperStyles}
            onDragEnter={() => {
              if (canAttach) {
                setActiveModal(ModalType.AttachmentsUploader);
              }
            }}
          >
            <SwitchConversationChip conversationId={conversationId} />
            <Modal.Body css={modalBodyStyles}>{bodyContent}</Modal.Body>
            <Box css={{ flexShrink: 0, marginTop: '-layout.looser' }}>
              <MessageListActions
                conversationId={conversationId}
                listingAlias={activeListingAlias}
                leadId={leadId}
                clientTimeZone={clientTimeZone}
                canSendMessage={canSendMessage}
                sendDisabledMessage={sendDisabledMessage}
                variant="compact"
                triggerLocation={TRIGGER_LOCATION.LeadManagementPage}
              />
            </Box>
            <Box css={{ flexShrink: 0 }}>
              <Divider />
            </Box>
            <Modal.Footer>{footerContent}</Modal.Footer>
          </Box>
        </Modal.Content>
      </Modal.Portal>
    </Modal.Root>
  );
}
